<template>
  <ul class="my-travel-list">
    <li class="my-travel-list__node">
      <span>
        <svg class="my-travel-list__calendar">
          <use :xlink:href="`#${calendar}`" />
        </svg>
        {{ travelDate }}
      </span>
    </li>
    <li class="my-travel-list__node">
      <span>
        <svg class="my-travel-list__time">
          <use :xlink:href="`#${time}`" />
        </svg>
        {{ travelData.departureTime }} - {{ travelData.arrivalTime }}
      </span>
      {{ travelData.travelDuration }}
    </li>
    <li
      v-for="[type, info] in Object.entries(passengerCounts)"
      :key="type"
      class="my-travel-list__node passenger"
    >
      <span>
        <svg class="my-travel-list__ticket">
          <use :xlink:href="`#${ticket}`" />
        </svg>
        <span class="my-travel-list__ticket-name">
          {{ getTranslatedPassengerName(type, info) }}
        </span>
      </span>
      <span v-if="!isInRebookingMode">
        {{ formatPrice(info.price) }}
      </span>
    </li>
    <li
      v-for="equipment in filteredEquipment"
      :key="equipment.product_code"
      class="my-travel-list__node equipment"
    >
      <span>
        <svg class="my-travel-list__equipment">
          <use :xlink:href="`#${luggage}`" />
        </svg>
        {{
          t('travelPlanner.myTravel.list.equipment.count', {
            count: equipment.count,
            type: getEquipmentName(equipment),
          })
        }}
      </span>
      {{ formatPrice(getEquipmentPrice(equipment)) }}
    </li>
    <li
      v-if="selectedSeats.length > 0"
      class="my-travel-list__node"
    >
      <span>
        <svg class="my-travel-list__seat">
          <use :xlink:href="`#${seat}`" />
        </svg>
        {{ selectedSeats.length }} {{ t('travelPlanner.myTravel.list.seats') }}
      </span>
      {{ formatPrice(totalSeatPrice) }}
    </li>
    <li
      v-if="showDiscounts && activeDiscounts.length > 0"
      class="my-travel-list__node discount"
      v-for="discount in activeDiscounts"
      :key="discount.code"
    >
      <span>
        <svg class="my-travel-list__discount">
          <use :xlink:href="`#${discount}`" />
        </svg>
        {{ discount.name }}
      </span>
    </li>
    <li
      v-if="showRebookingFee"
      class="my-travel-list__node rebooking-fee"
    >
      <span>
        <svg class="my-travel-list__fee">
          <use :xlink:href="`#${fee}`" />
        </svg>
        {{ t('travelPlanner.myTravel.list.rebookingFee') }}
      </span>
      {{ formatPrice(rebookingFee) }}
    </li>
    <li
      v-if="showTotal"
      class="my-travel-list__node my-travel-list__total"
    >
      <span>{{ t('travelPlanner.myTravel.list.total') }}</span>

      <span>
        {{ formatPrice(displayTotalPrice) }}
      </span>
    </li>
  </ul>
</template>

<script lang="ts">
  import { defineComponent, PropType, computed, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import type { SelectedSeat } from '@/types/seatTypes'
  import type { Ticket } from '@/types/bookingTypes'
  import {
    NormalizedPrice,
    formatNormalizedPrice,
  } from '@/utils/priceNormalization'
  import calendar from '@/assets/icons/calendar.svg'
  import time from '@/assets/icons/time.svg'
  import ticket from '@/assets/icons/ticket.svg'
  import luggage from '@/assets/icons/luggage.svg'
  import seat from '@/assets/icons/seat.svg'
  import discount from '@/assets/icons/discount.svg'
  import fee from '@/assets/icons/fee.svg'
  import { useMyTravelStore } from '@/stores/myTravelStore'
  import { useBookingStore } from '@/stores/bookingStore'

  interface PassengerFare {
    passenger_id: string
    price: NormalizedPrice | null
    original_price?: NormalizedPrice | null
    inventory_class?: string
    tariff_code: string
  }

  interface Equipment {
    id?: string
    product_type: string
    product_code: string
    product_family?: string
    comfort_zone?: string
    name: string
    passenger_fares: PassengerFare[]
    tariff_code: string
    count: number
    price: NormalizedPrice | null
    cancelled?: boolean | string | number
    uuid?: string
  }

  interface PassengerInfo {
    count: number
    price: NormalizedPrice | null
    name: string
    product_family: string
  }

  export default defineComponent({
    name: 'MyTravelList',
    props: {
      travelDate: {
        type: String,
        required: true,
      },
      travelData: {
        type: Object as PropType<{
          departureTime: string
          arrivalTime: string
          travelDuration: string
        }>,
        required: true,
      },
      direction: {
        type: String as PropType<'outbound' | 'inbound'>,
        required: true,
      },
      passengerCounts: {
        type: Object as PropType<Record<string, PassengerInfo>>,
        required: true,
      },
      groupedEquipment: {
        type: Array as PropType<Equipment[]>,
        default: () => [],
      },
      selectedSeats: {
        type: Array as PropType<SelectedSeat[]>,
        default: () => [],
      },
      totalSeatPrice: {
        type: Object as PropType<NormalizedPrice | null>,
        default: () => ({
          amount: 0,
          currency: 'NOK',
        }),
      },
      totalEquipmentPrice: {
        type: Object as PropType<NormalizedPrice | null>,
        default: () => ({
          amount: 0,
          currency: 'NOK',
        }),
      },
      selectedTickets: {
        type: Array as PropType<Ticket[]>,
        default: () => [],
      },
      discounts: {
        type: Array as PropType<any[]>,
        default: () => [],
      },
      totalPriceToBePaid: {
        type: Object as PropType<NormalizedPrice | null>,
        default: () => ({
          amount: 0,
          currency: 'NOK',
        }),
      },
      showTotal: {
        type: Boolean,
      },
      showDiscounts: {
        type: Boolean,
        default: false,
      },
    },
    setup(props) {
      const { t } = useI18n()
      const myTravelStore = useMyTravelStore()
      const bookingStore = useBookingStore()

      const activeDiscounts = computed(() =>
        props.discounts.filter((d) => d.amount > 0)
      )

      const rebookingFee = computed(() => {
        const isOutbound =
          props.travelData?.departureTime ===
          myTravelStore.outboundTravelData?.departureTime

        if (isOutbound) {
          return myTravelStore.outboundRebookingFee
        } else {
          return myTravelStore.inboundRebookingFee
        }
      })

      const showRebookingFee = computed(() => {
        const isInRebookingMode = myTravelStore.isInRebookingMode
        const hasFee =
          rebookingFee.value !== null && rebookingFee.value.amount > 0
        return isInRebookingMode && hasFee
      })

      const isRebooking = computed(() => {
        const urlParams = new URLSearchParams(window.location.search)
        return (
          urlParams.get('isRebooking') === 'true' ||
          !!myTravelStore.originalBookingPrice?.amount
        )
      })

      const getPassengerPrice = (
        passengerInfo: PassengerInfo | null,
        isRebooking: boolean
      ) => {
        if (isRebooking && passengerInfo && passengerInfo.price) {
          return {
            ...passengerInfo.price,
            amount: passengerInfo.price.amount || 0,
          }
        }
        return passengerInfo?.price || null
      }

      const adjustedPassengerCounts = computed(() => {
        if (!isRebooking.value) {
          return props.passengerCounts
        }

        const adjusted: Record<string, PassengerInfo> = {}

        for (const [type, info] of Object.entries(props.passengerCounts)) {
          adjusted[type] = {
            ...info,
            price: getPassengerPrice(info, true),
          }
        }
        return adjusted
      })

      const isInRebookingMode = computed(() => {
        return myTravelStore.isRebooking
      })

      const totalTicketPrice = computed(() => {
        return myTravelStore.totalPrice?.amount ?? 0
      })

      const displayTotalPrice = computed(() => {
        if (props.totalPriceToBePaid?.amount === 0) {
          return {
            amount: 0,
            currency: 'NOK',
          }
        }

        if (isInRebookingMode.value) {
          const ticketPrice = totalTicketPrice.value
          const seatPrice = myTravelStore.totalSeatPriceInKroner || 0
          const equipmentPrice = myTravelStore.totalEquipmentPriceInKroner || 0

          const totalAmount = ticketPrice + seatPrice + equipmentPrice

          return {
            amount: totalAmount,
            currency: 'NOK',
          }
        }

        const ticketPrice = myTravelStore.totalPrice?.amount ?? 0
        const seatPrice = myTravelStore.totalSeatPriceInKroner || 0
        const equipmentPrice = myTravelStore.totalEquipmentPriceInKroner || 0
        const discountAmount = myTravelStore.totalDiscountsAmount || 0
        const totalAmount = ticketPrice + seatPrice + equipmentPrice
        const totalAfterDiscounts = Math.max(0, totalAmount - discountAmount)

        if (props.showTotal) {
          if (props.totalPriceToBePaid) {
            if (props.totalPriceToBePaid.amount > 10000) {
              console.error(
                '[MyTravelList] ERROR: Price appears to be in øre instead of kroner:',
                props.totalPriceToBePaid.amount
              )
              return {
                amount: props.totalPriceToBePaid.amount,
                currency: 'NOK (ERROR: øre?)',
              }
            }

            return props.totalPriceToBePaid
          }
          return {
            amount: totalAfterDiscounts,
            currency: 'NOK',
          }
        }

        if (props.totalPriceToBePaid) {
          if (props.totalPriceToBePaid.amount > 10000) {
            return {
              amount: props.totalPriceToBePaid.amount,
              currency: 'NOK (ERROR: øre?)',
            }
          }
          return props.totalPriceToBePaid
        }

        return {
          amount: 0,
          currency: 'NOK',
        }
      })

      const getPassengerType = (type: string) => {
        const typeMap: Record<string, string> = {
          ADT: t('common.passenger.adult'),
          CHD: t('common.passenger.child'),
          YOU: 'Ungdom',
          STU: t('common.passenger.student'),
          SEN: t('common.passenger.senior'),
          CON: t('common.passenger.conscript'),
          CPN: t('common.passenger.companion'),
          COM: t('common.passenger.companion'),
        }

        if (typeMap[type]) {
          return typeMap[type]
        }

        const i18nKey = `descriptions.passengerTypes.${type}`
        const translatedType = t(i18nKey)
        if (translatedType !== i18nKey) {
          return translatedType
        }

        return type
      }

      const getTranslatedPassengerName = (
        type: string,
        info: PassengerInfo
      ) => {
        const productFamily = info.product_family || ''
        const translatedType = getPassengerType(type)

        if (info.name) {
          const countPattern = /^(\d+)\s+([\w\s]+)\s+-\s+(.+)$/
          const countMatch = info.name.match(countPattern)

          if (countMatch) {
            return info.name
          }

          if (productFamily && info.name.includes(productFamily)) {
            return info.name
          }

          const nameParts = info.name.split(' - ')
          if (nameParts.length > 1) {
            return `${translatedType} - ${nameParts[1]}`
          }
        }

        if (productFamily) {
          return `${translatedType} - ${productFamily}`
        }

        return translatedType
      }

      const getEquipmentName = (equipment: Equipment) => {
        let translation = t(
          `descriptions.equipmentTypes.${equipment.product_code}.displayName`
        )

        if (
          translation ===
          `descriptions.equipmentTypes.${equipment.product_code}.displayName`
        ) {
          translation = equipment.product_code
        }

        return translation
      }

      const getEquipmentPrice = (equipment: Equipment) => {
        // Special case handling for known products with fixed prices
        if (
          equipment.product_code === 'SPECIAL_LUGGAGE' ||
          (equipment.name && equipment.name.includes('Spesialbagasje'))
        ) {
          return {
            amount: 270,
            currency: 'NOK',
          }
        }

        if (
          equipment.product_code === 'LUGGAGE' ||
          (equipment.name && equipment.name.includes('Ekstra bagasje'))
        ) {
          return {
            amount: 50,
            currency: 'NOK',
          }
        }

        if (equipment.product_code === 'seter') {
          return {
            amount: 59,
            currency: 'NOK',
          }
        }

        // For Endringsgebyr, return 39 NOK
        if (equipment.name && equipment.name.includes('Endringsgebyr')) {
          return {
            amount: 39,
            currency: 'NOK',
          }
        }

        // Normal price handling
        let priceObject = equipment.price
        let priceAmount = priceObject?.amount || 0
        let priceSource = 'direct'

        // If direct price is zero or missing, try to get from passenger_fares
        if (
          (!priceObject || priceAmount === 0) &&
          equipment.passenger_fares?.length > 0
        ) {
          const passengerFare = equipment.passenger_fares[0]
          priceObject = passengerFare?.price
          priceAmount = priceObject?.amount || 0
          priceSource = 'passenger_fares'
          console.log(
            `[DEBUG] MyTravelList: Using price from passenger_fares for ${equipment.product_code}, amount=${priceAmount}`
          )
        }

        if (!priceObject || priceAmount === 0) {
          console.log(
            `[DEBUG] MyTravelList: No price found for ${equipment.product_code}`
          )
          return null
        }

        // Check if the price is likely in øre and needs conversion to kroner
        const isLikelyInOre = priceAmount > 1000
        if (isLikelyInOre) {
          priceAmount = priceAmount / 100
          console.log(
            `[DEBUG] MyTravelList: Converted price from øre to kroner for ${equipment.product_code}, new amount=${priceAmount}`
          )
        }

        const finalPrice = {
          amount: priceAmount * equipment.count,
          currency: priceObject.currency || 'NOK',
        }

        console.log(
          `[DEBUG] MyTravelList: Final price for ${equipment.product_code}: ${finalPrice.amount} ${finalPrice.currency}`
        )
        return finalPrice
      }

      const formatPrice = (price: NormalizedPrice | null) => {
        if (!price) {
          return '0,-'
        }

        if (typeof price.amount !== 'number') {
          return '0,-'
        }

        const priceCopy = { ...price }

        if (isNaN(priceCopy.amount)) {
          return '0,-'
        }

        // Allow negative values to be displayed
        if (priceCopy.amount === 0) {
          return '0,-'
        }

        // For negative values, format with a minus sign
        const formattedPrice = formatNormalizedPrice(priceCopy)
        return formattedPrice
      }

      const filteredEquipment = computed(() => {
        if (!props.groupedEquipment) return []

        const filtered = props.groupedEquipment.filter((item) => {
          const productType = (item.product_type || '').toUpperCase()
          const type = (item.type || '').toUpperCase()
          const productCode = (item.product_code || '').toUpperCase()
          const name = (item.name || '').toUpperCase()

          // Log for debugging
          console.log(
            `[DEBUG] MyTravelList: Filtering item ${
              item.name || item.code || item.product_code
            }, product_type=${productType}, type=${type}, product_code=${productCode}`
          )

          // Check if this is a seat product by any identifying field
          if (
            productType === 'SF' ||
            productType === 'SEAT' ||
            type === 'SF' ||
            type === 'SEAT' ||
            productCode === 'COMFORT_SEAT' ||
            productCode === 'FRONT_SEAT'
          ) {
            // Special case: keep the 'seter' product with price 59,-
            if (
              productCode === 'SETER' ||
              (item.product_code === 'seter' && item.price?.amount === 59)
            ) {
              console.log(
                `[DEBUG] MyTravelList: Keeping special 'seter' product: ${item.product_code}`
              )
              return true
            }

            console.log(
              `[DEBUG] MyTravelList: Filtering out seat product: ${
                item.name || item.code || item.product_code
              }`
            )
            return false
          }

          // Filter out cancelled bicycle products
          if (
            item.product_code === 'BICYCLE' ||
            item.product_code === 'SYKKEL' ||
            (item.name && item.name.toLowerCase().includes('sykkel'))
          ) {
            // If it has zero price or is cancelled, filter it out
            if (!item.price?.amount || item.price.amount === 0) {
              console.log(
                `[DEBUG] MyTravelList: Filtering out bicycle with zero price: ${
                  item.name || item.code || item.product_code
                }`
              )
              return false
            }
          }

          // Check if the product is cancelled
          let isCancelled = false

          // Check direct cancelled property
          if (typeof item.cancelled === 'boolean') {
            isCancelled = item.cancelled === true
          } else if (typeof item.cancelled === 'string') {
            isCancelled = item.cancelled === 'true' || item.cancelled === '1'
          } else if (typeof item.cancelled === 'number') {
            isCancelled = item.cancelled === 1
          }

          // Products with count 0 are considered cancelled
          if (!isCancelled && item.count === 0) {
            console.log(
              `[DEBUG] MyTravelList: Product with count 0 considered cancelled: ${
                item.name || item.code || item.product_code
              }`
            )
            isCancelled = true
          }

          // Check cancellation status in booking segments
          if (!isCancelled && item.uuid && bookingStore.bookingData) {
            const bookingSegments =
              bookingStore.bookingData?.[
                props.direction === 'outbound'
                  ? 'outbound_booking_tariff_segments'
                  : 'inbound_booking_tariff_segments'
              ] || []

            for (const segment of bookingSegments) {
              const additionalProducts = segment.additional_products || []
              const matchingProduct = additionalProducts.find(
                (ap) =>
                  ap.uuid === item.uuid ||
                  ('product_code' in ap &&
                    ap.product_code === item.product_code)
              )

              if (matchingProduct) {
                let isProductCancelled = false

                if (typeof matchingProduct.cancelled === 'boolean') {
                  isProductCancelled = matchingProduct.cancelled === true
                } else if (typeof matchingProduct.cancelled === 'string') {
                  isProductCancelled =
                    matchingProduct.cancelled === 'true' ||
                    matchingProduct.cancelled === '1'
                } else if (typeof matchingProduct.cancelled === 'number') {
                  isProductCancelled = matchingProduct.cancelled === 1
                }

                if (isProductCancelled) {
                  isCancelled = true
                  break
                }
              }
            }
          }
          return !isCancelled
        })

        return filtered
      })
      return {
        passengerCounts: adjustedPassengerCounts,
        activeDiscounts,
        displayTotalPrice,
        getPassengerType,
        getTranslatedPassengerName,
        getEquipmentName,
        getEquipmentPrice,
        formatPrice,
        rebookingFee,
        showRebookingFee,
        isInRebookingMode,
        totalTicketPrice,
        filteredEquipment,
        calendar,
        time,
        ticket,
        luggage,
        seat,
        discount,
        fee,
        t,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .my-travel-list {
    list-style: none;
    margin: 0;
    padding: 0;

    &__node {
      @include body-medium;
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: rem(16);
      color: inherit;

      svg {
        margin-right: rem(8);
      }

      span {
        display: flex;
        align-items: center;
      }
    }

    &__ticket-name {
      margin-left: rem(4);
      color: $clr-primary-600;
    }

    &__calendar {
      width: 16px;
      height: 16px;
    }

    &__time {
      width: 20px;
      height: 20px;
    }

    &__ticket {
      width: 18px;
      height: 15px;
    }

    &__equipment {
      width: 18px;
      height: 18px;
    }

    &__discount {
      color: $clr-text;
      width: 18px;
      height: 18px;
    }

    &__fee {
      width: 18px;
      height: 18px;
      color: $clr-primary;
    }

    &__seat {
      width: 1.25rem;
      height: 1.25rem;
      margin-right: 0.5rem;
    }

    &__total {
      @include title-medium;
      border-top: solid 1px $clr-text;
      padding-top: 24px;
    }
  }
</style>
